import { fetchPopularAirports } from 'actions/FlightActions'
import DropdownList from 'LuxKit/Dropdown/List/DropdownList'
import { useAppDispatch } from 'hooks/reduxHooks'
import { useScreenSizeOnly } from 'hooks/useScreenSize'
import type { ChangeEventHandler, ComponentProps } from 'react'
import React, { useCallback, useEffect, useRef, useState } from 'react'
import { useIntl } from 'react-intl'
import OfferAirportOptionsList from './OfferAirportOptionsList'
import TextInputOnly from 'components/Common/Form/Input/TextInputOnly'
import LineSearchIcon from 'LuxKit/Icons/line/LineSearchIcon'

interface Props extends Pick<ComponentProps<typeof DropdownList>, 'open' | 'anchorRef' | 'triggerRef'> {
  onClose: () => void
  onSelectAirport: (airport: App.AirportLocation) => void
  selectedAirportCode?: string
}

function OfferAirportSelectDropdown(props: Props) {
  const {
    open,
    anchorRef,
    triggerRef,
    onClose,
    onSelectAirport,
  } = props

  const inputRef = useRef<HTMLInputElement>(null)

  const dispatch = useAppDispatch()
  const isMobile = useScreenSizeOnly('mobile')
  const intl = useIntl()

  useEffect(() => {
    if (open && !isMobile) {
      inputRef.current?.focus()
    }
  }, [isMobile, open])

  useEffect(() => {
    dispatch(fetchPopularAirports())
  }, [dispatch])

  const [searchPhrase, setSearchPhrase] = useState('')

  const handleSearchPhraseInputChange = useCallback<ChangeEventHandler<HTMLInputElement>>((event) => {
    setSearchPhrase(event.target.value)
  }, [])

  const title = intl.formatMessage({
    id: 'GzG9+EdQEC',
    defaultMessage: 'Where will you depart from?',
    description: 'Title for airport selection dropdown',
  })

  const subtitle = intl.formatMessage({
    id: 'D2uGry66sE',
    defaultMessage: 'Select your preferred airport',
    description: 'Subtitle for airport selection dropdown',
  })

  const applyButtonText = intl.formatMessage({
    id: 'T3MQn7j5/i',
    defaultMessage: 'Apply',
    description: 'Button text to apply airport selection',
  })

  const searchPlaceholder = intl.formatMessage({
    id: 'ZhKyXXa6vD',
    defaultMessage: 'Search by city or airport',
    description: 'Placeholder text for airport search input',
  })

  return <DropdownList
    size="M"
    open={open && !isMobile}
    anchorRef={anchorRef}
    triggerRef={triggerRef}
    title={title}
    subtitle={subtitle}
    onClose={onClose}
    primaryActionProps={{
      children: applyButtonText,
      onClick: onClose,
    }}
    headerExtension={<TextInputOnly
      placeholder={searchPlaceholder}
      noValidationMessage
      noValidationSpacing
      onChange={handleSearchPhraseInputChange}
      endIcon={<LineSearchIcon />}
      ref={inputRef}
    />}
  >
    <OfferAirportOptionsList
      searchPhrase={searchPhrase}
      onSelectAirport={onSelectAirport}
    />
  </DropdownList>
}

export default React.memo(OfferAirportSelectDropdown)
